import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

interface Course {
  name: string;
  classes: { [key: string]: string };
  icon: string;
  color: string;
  bgGradient: string;
  accentColor: string;
  buttonGradient: string;
  description: string;
}

const coursesData: Course[] = [
  {
    name: "SCIENCE",
    classes: {
      "Class 11": "SCIENCE 11",
      "Class 12": "SCIENCE 12",
    },
    icon: "/assets/imgsci5.png",
    color: "blue",
    bgGradient: "bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-400",
    accentColor: "blue",
    buttonGradient: "bg-gradient-to-r from-blue-600 to-cyan-400",
    description: "Explore advanced science subjects with comprehensive study materials."
  },
  {
    name: "ARTS",
    classes: {
      "Class 11": "ARTS 11",
      "Class 12": "ARTS 12",
    },
    icon: "/assets/imgsci2.png",
    color: "purple",
    bgGradient: "bg-gradient-to-br from-fuchsia-600 via-purple-500 to-pink-400",
    accentColor: "purple",
    buttonGradient: "bg-gradient-to-r from-fuchsia-600 to-pink-400",
    description: "Discover the creative world of arts with our specialized curriculum."
  },
  {
    name: "FOUNDATION",
    classes: {
      "Class 10": "CLASS 10",
      "Class 9": "CLASS 9",
      "Class 8": "CLASS 8",
    },
    icon: "/assets/imgsci2.png",
    color: "green",
    bgGradient: "bg-gradient-to-br from-emerald-600 via-green-500 to-teal-400",
    accentColor: "green",
    buttonGradient: "bg-gradient-to-r from-emerald-600 to-teal-400",
    description: "Build a strong academic foundation with our fundamental courses."
  },
  {
    name: "Problem Solving",
    classes: {
      "NEET 11": "NEET 11",
      "NEET 12": "NEET 12",
      "JEE 11": "JEE 11",
      "JEE 12": "JEE 12",
    },
    icon: "/assets/imgsci7.png",
    color: "orange",
    bgGradient: "bg-gradient-to-br from-orange-600 via-amber-500 to-yellow-400",
    accentColor: "orange",
    buttonGradient: "bg-gradient-to-r from-orange-600 to-yellow-400",
    description: "Master advanced problem-solving techniques for competitive exams."
  },
];

const ExploreByExam = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [activeCard, setActiveCard] = useState<string | null>(null);

  useEffect(() => {
    setCourses(coursesData);
  }, []);

  const navigateToCourse = (courseValue: string) => {
    console.log(`Navigating to course: ${courseValue}`);
  };

  return (
    <div className="container mx-auto px-6 py-16 bg-gradient-to-b from-indigo-50 via-purple-50 to-pink-50">
      <div className="relative mb-16 text-center">
        <h2 className="text-5xl font-extrabold text-center mb-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent drop-shadow-sm">
          Explore Your Learning Path
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Discover specialized courses designed to help you excel in your educational journey
        </p>
        <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 w-24 h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"></div>
      </div>
      
      <div className="flex flex-wrap justify-center items-stretch gap-8 mb-16">
        {courses.map((course) => (
          <div
            key={course.name}
            className={`w-96 rounded-3xl shadow-2xl overflow-hidden my-4 transition-all duration-500 ${
              hoveredCard === course.name 
                ? "transform scale-105 shadow-2xl z-10" 
                : activeCard === course.name 
                ? "ring-4 ring-opacity-50 ring-" + course.accentColor + "-400" 
                : ""
            }`}
            onMouseEnter={() => setHoveredCard(course.name)}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => setActiveCard(course.name === activeCard ? null : course.name)}
          >
            <div className={`p-6 ${course.bgGradient}`}>
              <div className="flex justify-between items-start">
                <h3 className="text-2xl font-bold mb-4 text-white">{course.name}</h3>
                <div className="bg-white bg-opacity-30 p-3 rounded-2xl backdrop-blur-sm shadow-lg transform transition-transform duration-300 hover:rotate-12 hover:scale-110">
                  <img
                    src={course.icon}
                    alt={course.name}
                    className="object-contain h-10 w-10"
                  />
                </div>
              </div>
              
              <p className="text-white text-opacity-90 mb-4 text-sm">{course.description}</p>
              
              <div className="w-full mt-4">
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(course.classes).map(([className, value]) => {
                    const buttonId = `${course.name}-${className}`;
                    return (
                      <Link to={`/coursesPage/${value}`} 
                        key={buttonId}
                        onMouseEnter={() => setHoveredButton(buttonId)}
                        onMouseLeave={() => setHoveredButton(null)}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateToCourse(value);
                        }}
                        className={`bg-white font-medium py-3 px-4 rounded-xl transition-all duration-300 text-${course.accentColor}-600 ${
                          hoveredButton === buttonId 
                            ? "shadow-lg transform scale-105 bg-opacity-100" 
                            : "bg-opacity-85"
                        }`}
                      >
                        {className}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 ">
              <div className="flex items-center justify-between">
                <div className={`font-medium text-${course.accentColor}-600`}>Explore more courses</div>
                <button className={`p-3 rounded-full ${course.buttonGradient} text-white transition-all duration-300 hover:scale-110 hover:rotate-12 shadow-md hover:shadow-lg`}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5">
                    <path fillRule="evenodd" d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-12 max-w-4xl mx-auto">
        <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-1 shadow-xl">
          <div className="bg-white rounded-xl p-8 flex flex-col md:flex-row items-center justify-between">
            <div className="text-center md:text-left md:max-w-md">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Ready to start your learning journey?
              </h3>
              <p className="mt-2 text-gray-600">Browse our complete catalog with over 200+ courses available</p>
            </div>
            <button className="mt-6 md:mt-0 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium py-3 px-8 rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105 group">
              View All Courses
              <span className="inline-block ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreByExam;